import { isEmpty, orderBy } from 'lodash';
import { fieldTypeOf, fieldTypes, settingsOf } from '../../field-types';
import { FieldTypeCard, hasOptions, PaletteGroup, PaletteItem } from '../../field-types/card';
import type { Element, FieldElement } from '../../format/form-definition';
import { sameJson } from '../same-json';
import NotesOutlined from '@mui/icons-material/NotesOutlined';
import ViewAgendaOutlined from '@mui/icons-material/ViewAgendaOutlined';
import WidgetsOutlined from '@mui/icons-material/WidgetsOutlined';
import type { ComponentType } from 'react';
import type { SvgIconProps } from '@mui/material';
import { IdPrefix } from '../../format/elements';

export const FIELDS_PALETTE_GROUPS: PaletteGroup[] = ['Text', 'Choice', 'Number, date and file', 'Layout'];
const DEFAULT_OPTIONS = ['Option 1', 'Option 2'];

interface ElementType {
  name: string;
  icon: ComponentType<SvgIconProps>;
}

type DrawId = (prefix: IdPrefix) => string;

export interface Tile extends ElementType {
  tooltip: string;
  group: PaletteGroup;
  create(draw: DrawId): Element;
}

const section: Tile = {
  name: 'Section',
  tooltip: 'A titled block of elements',
  group: 'Layout',
  icon: ViewAgendaOutlined,
  create: draw => ({ id: draw('sec'), kind: 'section', title: '', elements: [] }),
};

const staticText: Tile = {
  name: 'Static text',
  tooltip: 'Text for the respondent',
  group: 'Layout',
  icon: NotesOutlined,
  create: draw => ({ id: draw('txt'), kind: 'staticText', text: '' }),
};

interface FieldTile extends Tile {
  card: FieldTypeCard;
  item: PaletteItem;
}

const cards: FieldTypeCard[] = fieldTypes;

const fieldTiles: FieldTile[] = cards.flatMap(card =>
  card.palette.map(item => ({
    name: item.name,
    tooltip: item.tooltip,
    group: item.group,
    icon: item.icon,
    card,
    item,
    create: (draw: DrawId) => newField(card, item, draw),
  })),
);
export const TILES: Tile[] = [
  ...fieldTiles.filter(tile => tile.group !== 'Layout'),
  section,
  ...fieldTiles.filter(tile => tile.group === 'Layout'),
  staticText,
];

const newField = (card: FieldTypeCard, item: PaletteItem, draw: DrawId): FieldElement => {
  const holdsFields = card.valueKind === 'rows';
  const settings = {
    ...item.settings,
    ...(hasOptions(card.valueKind) && {
      options: DEFAULT_OPTIONS.map(label => ({ id: draw('opt'), label })),
    }),
  };
  const field = {
    id: draw(holdsFields ? 'grp' : 'fld'),
    kind: 'field',
    type: card.type,
    label: '',
    ...(holdsFields && { elements: [] }),
    ...(!isEmpty(settings) && { settings }),
    ...(item.validations && { validations: structuredClone(item.validations) }),
  };
  return field as FieldElement;
};

export const elementType = (element: Element): ElementType => {
  if (element.kind === 'section') return section;
  if (element.kind === 'staticText') return staticText;
  const card = fieldTypeOf(element);
  const settings = settingsOf(element);
  const presets = (tile: FieldTile) => Object.entries(tile.item.settings ?? {});
  const fits = (tile: FieldTile) =>
    tile.card === card && presets(tile).every(([key, value]) => sameJson(settings[key], value));
  const fitting = orderBy(fieldTiles.filter(fits), tile => presets(tile).length, 'desc');
  return fitting[0] ?? { name: card.name, icon: WidgetsOutlined };
};
